/**
 * Browser-side image compression.
 *
 * Goal: ensure final base64 string under ~200 KB so even free MongoDB tiers won't choke
 * on hotel galleries. We progressively scale down + reduce JPEG quality until we hit the
 * target. EXIF orientation is honoured via `createImageBitmap({ imageOrientation: 'from-image' })`
 * with a canvas fallback.
 *
 * Usage:
 *   const { dataUrl, sizeKb } = await compressImage(file, { targetKb: 200, maxDim: 1600 });
 */

const DEFAULT_MAX_DIM = 1600;       // longest side after resize
const DEFAULT_TARGET_KB = 200;      // shoot for ≤200 KB

const dataUrlSizeKb = (dataUrl) => Math.round((dataUrl.length * 3) / 4 / 1024);

async function fileToBitmap(file) {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    // Safari < 17 fallback — use Image element
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = url;
      });
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

function drawToCanvas(bitmap, maxDim) {
  const srcW = bitmap.width || bitmap.naturalWidth;
  const srcH = bitmap.height || bitmap.naturalHeight;
  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const w = Math.round(srcW * scale);
  const h = Math.round(srcH * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas;
}

/**
 * Compress a File/Blob to a base64 data URL.
 * - If file <100 KB already, returns the original encoded as-is.
 * - Otherwise iteratively reduces dimensions + JPEG quality until sizeKb ≤ targetKb.
 *
 * Returns: { dataUrl, sizeKb, width, height, originalKb }
 */
export async function compressImage(
  file,
  { targetKb = DEFAULT_TARGET_KB, maxDim = DEFAULT_MAX_DIM, mimeType = 'image/jpeg' } = {}
) {
  const originalKb = Math.round(file.size / 1024);
  if (file.size <= 100 * 1024 && file.type.startsWith('image/')) {
    // Already tiny — base64-encode untouched
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    return { dataUrl, sizeKb: dataUrlSizeKb(dataUrl), originalKb, compressed: false };
  }

  const bitmap = await fileToBitmap(file);

  // Try a sequence of (dimension, quality) pairs until we are at or below targetKb.
  const attempts = [
    [maxDim, 0.82],
    [maxDim, 0.7],
    [Math.round(maxDim * 0.85), 0.7],
    [Math.round(maxDim * 0.72), 0.65],
    [Math.round(maxDim * 0.6), 0.6],
    [Math.round(maxDim * 0.5), 0.55],
    [Math.round(maxDim * 0.4), 0.5],
  ];

  let best = null;
  for (const [dim, q] of attempts) {
    const canvas = drawToCanvas(bitmap, dim);
    const dataUrl = canvas.toDataURL(mimeType, q);
    const sizeKb = dataUrlSizeKb(dataUrl);
    if (!best || sizeKb < best.sizeKb) {
      best = { dataUrl, sizeKb, width: canvas.width, height: canvas.height };
    }
    if (sizeKb <= targetKb) break;
  }

  return { ...best, originalKb, compressed: true };
}

/** Compress many files in parallel. Returns array of compression results in input order. */
export async function compressManyImages(files, opts) {
  return Promise.all(Array.from(files).map((f) => compressImage(f, opts)));
}
